import React, { useEffect, useRef, useState } from 'react';
import DefaultButton from 'components/Utils/NewDefaultButton';
import './style.scss';

const CHECK_INTERVAL_MS = 15 * 60 * 1000;
const MIN_CHECK_GAP_MS = 5 * 60 * 1000;
const SNOOZE_INTERVAL_MS = 30 * 60 * 1000;

// Only meaningful against a deployed bucket: locally there is no version.json
// and __APP_VERSION__ is 'dev'
const ENABLED = import.meta.env.PROD && __APP_VERSION__ !== 'dev';

/**
 * Polls /version.json (uploaded with no-store alongside index.html) and shows
 * a banner prompting a refresh when the deployed build differs from the one
 * currently running. Complements the BE-forced 418/oldVersion flow.
 */
const NewVersionBanner = () => {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const lastCheck = useRef(0);
  const snoozedUntil = useRef(0);

  useEffect(() => {
    if (!ENABLED) return undefined;

    let cancelled = false;

    const check = async () => {
      const now = Date.now();
      if (now - lastCheck.current < MIN_CHECK_GAP_MS) return;
      lastCheck.current = now;
      try {
        const res = await fetch(`/version.json?ts=${now}`, { cache: 'no-store' });
        if (!res.ok) return;
        const { version } = await res.json();
        if (!cancelled && version && version !== __APP_VERSION__) {
          setNewVersionAvailable(true);
          if (now > snoozedUntil.current) setDismissed(false);
        }
      } catch {
        // offline or transient network error, retry on next tick
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };

    // A hashed chunk from this build failed to load: it was most likely
    // replaced by a newer deploy, so re-surface the banner even if dismissed
    const onPreloadError = () => {
      setNewVersionAvailable(true);
      setDismissed(false);
    };

    const interval = setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('vite:preloadError', onPreloadError);
    check();

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('vite:preloadError', onPreloadError);
    };
  }, []);

  if (!newVersionAvailable || dismissed) return null;

  return (
    <div className="new-version-banner">
      <span className="message">A new version of the app is available.</span>
      <DefaultButton
        name="Refresh"
        className="refresh-button"
        onClick={() => window.location.reload()}
      />
      <DefaultButton
        name="Later"
        className="later-button"
        onClick={() => {
          snoozedUntil.current = Date.now() + SNOOZE_INTERVAL_MS;
          setDismissed(true);
        }}
      />
    </div>
  );
};

export default NewVersionBanner;
